<template>
  <v-container class="ma-0 pa-0 mw-100">
    <v-row>
      <v-col cols="12">
        <title-big :title="$t('HomePage.header.register')"></title-big>
      </v-col>
    </v-row>

    <v-row align="center" justify="center">
      <v-col cols="12">
        <v-card class="elevation-12">
          <v-toolbar flat>
            <v-toolbar-title>
              <title-medium
                v-if="newOrg"
                :title="$t('RegisterPage.headline_1')"
              ></title-medium>
              <title-medium
                v-else
                :title="$t('RegisterPage.headline_2')"
              ></title-medium>
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <validation-observer ref="observer" v-slot="{ invalid }">
              <v-form ref="form" data-cy="registerForm">
                <v-container>
                  <v-container class="ma-0 pa-0 mw-100" v-if="newOrg">
                    <p>
                      {{ $t('RegisterPage.info_1') }}
                      <custom-link
                        link="/register"
                        :text="$t('RegisterPage.link_text')"
                        target="_self"
                      ></custom-link>
                      {{ $t('RegisterPage.info_2') }}
                    </p>
                    <p>
                      {{ $t('RegisterPage.info_3') }}
                    </p>
                  </v-container>
                  <v-container class="ma-0 pa-0 mw-100" v-if="!newOrg">
                    <p>
                      {{ $t('RegisterPage.info_4') }}
                      <custom-link
                        link="/register/new-org"
                        :text="$t('RegisterPage.link_text')"
                        target="_self"
                      ></custom-link>
                      {{ $t('RegisterPage.info_5') }}
                    </p>
                  </v-container>
                  <v-row>
                    <v-col cols="12" sm="6">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('Headers.first_name')"
                        rules="required|name_validate"
                      >
                        <v-text-field
                          filled
                          :label="$t('Headers.first_name')"
                          v-model="user.first_name"
                          :hint="$t('Hints.first_name')"
                          required
                          :error-messages="errors"
                          data-cy="registerFirstName"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('Headers.last_name')"
                        rules="required|name_validate"
                      >
                        <v-text-field
                          filled
                          :label="$t('Headers.last_name')"
                          v-model="user.last_name"
                          :hint="$t('Hints.last_name')"
                          required
                          :error-messages="errors"
                          data-cy="registerLastName"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('Headers.email')"
                        rules="required|email"
                      >
                        <v-text-field
                          filled
                          :label="$t('Headers.email')"
                          v-model="user.e_mail"
                          :hint="$t('Hints.email')"
                          required
                          :error-messages="errors"
                          data-cy="registerEmail"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <vue-tel-input-vuetify
                        filled
                        v-model="user.phone"
                        @input="checkPhone"
                        defaultCountry="DE"
                        :placeholder="$t('Headers.phone')"
                        :label="$t('Headers.phone')"
                        :error-messages="errorsPhone"
                        mode="international"
                        data-cy="contactPhone"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('Headers.password')"
                        vid="password"
                        rules="required|password"
                      >
                        <v-text-field
                          filled
                          :label="$t('Headers.password')"
                          v-model="user.password"
                          type="password"
                          :hint="$t('RegisterPage.form_info_3')"
                          required
                          :error-messages="errors"
                          data-cy="registerPassword"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('RegisterPage.form_label_1')"
                        rules="required|confirmed:password"
                      >
                        <v-text-field
                          filled
                          :label="$t('RegisterPage.form_label_1')"
                          type="password"
                          v-model="user.confirm_password"
                          required
                          :error-messages="errors"
                          data-cy="registerConfirmPassword"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" sm="6" v-if="!newOrg">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('Headers.license_key')"
                        rules="required"
                      >
                        <v-text-field
                          filled
                          :label="$t('Headers.license_key')"
                          v-model="license_key_id"
                          required
                          :error-messages="errors"
                          data-cy="registerLicenseKey"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('Headers.language')"
                        rules="required"
                      >
                        <v-select
                          filled
                          :label="$t('Headers.language')"
                          v-model="user.language_id"
                          :items="languages"
                          :error-messages="errors"
                          required
                        ></v-select>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" sm="6" v-if="newOrg">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('AdminPage.CompanyName')"
                        rules="required|name_validate"
                      >
                        <v-text-field
                          filled
                          :label="$t('AdminPage.CompanyName')"
                          v-model="company.name"
                          :hint="$t('Hints.company_name')"
                          required
                          :error-messages="errors"
                          data-cy="registerName"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12">
                      <validation-provider
                        v-slot="{ errors }"
                        :rules="{ required: { allowFalse: false } }"
                      >
                        <v-checkbox
                          v-model="privacyCheck"
                          color="rgb(30, 101, 105)"
                          @click="setEnable"
                          :error-messages="errors"
                        >
                          <span slot="label">
                            {{ $t('RegisterPage.gdpr_2') }}
                            <custom-link
                              link="https://sign8.eu"
                              :text="$t('RegisterPage.gdpr')"
                              target="_blank"
                            ></custom-link>
                            {{ $t('RegisterPage.gdpr_3') }}
                          </span>
                        </v-checkbox>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row justify="center">
                    <v-col class="text-right" cols="12" sm="10">
                      <custom-button
                        :loading="loadingState"
                        @click="cancel"
                        :text="$t('Headers.cancel')"
                      />
                    </v-col>
                    <v-col class="text-right" cols="12" sm="2">
                      <custom-button
                        :disabled="invalid"
                        :loading="loadingState"
                        @click="register"
                        :text="$t('RegisterPage.register_button')"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </validation-observer>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import CustomButton from '../../components/ui/input/CustomButton'
import TitleBig from '../../components/ui/text/HeaderBig'
import TitleMedium from '../../components/ui/text/HeaderMedium'
import CustomLink from '../../components/ui/text/HyperLink.vue'
import i18n from '@/i18n'
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode,
} from 'vee-validate'
import { required, email, confirmed } from 'vee-validate/dist/rules'
import { log } from '@/helpers/logger'

setInteractionMode('eager')

extend('required', {
  ...required,
  message: i18n.t('FormValidation.required'),
})

extend('email', {
  ...email,
  message: i18n.t('FormValidation.email'),
})

extend('name_validate', {
  validate: (value) =>
    /(^[a-zA-ZäÄöÖüÜßàÀèÈìÌòÒùÙçÇãÃñÑõÕëËïÏâÂêÊîÎôÔûÛáÁéÉíÍóÓúÚýÝ\-.\s]+)$/.test(
      value
    ),
  message: i18n.t('FormValidation.name'),
})

extend('text', {
  validate: (value) =>
    /(^[a-zA-Z0-9äÄöÖüÜßàÀèÈìÌòÒùÙçÇãÃñÑõÕëËïÏâÂêÊîÎôÔûÛáÁéÉíÍóÓúÚýÝ\-.\s]+)$/.test(
      value
    ),
  message: i18n.t('FormValidation.field'),
})

extend('password', {
  validate: (value) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^])[A-Za-z\d!@#$%^]{8,}$/.test(
      value
    ),
  message: i18n.t('FormValidation.password'),
})

extend('confirmed', {
  ...confirmed,
  message: 'Passwords must match',
})

export default {
  name: 'Register',
  title: i18n.t('HomePage.header.register'),
  components: {
    CustomButton,
    TitleBig,
    TitleMedium,
    CustomLink,
    ValidationProvider,
    ValidationObserver,
  },
  props: ['new_org'],
  data() {
    return {
      privacyCheck: false,
      user: {
        first_name: '',
        last_name: '',
        e_mail: '',
        phone: '',
        password: '',
        confirm_password: '',
        account_role: '',
      },
      company: {
        name: '',
        salesTaxNumber: '',
        contactPerson: '',
      },
      license_key_id: '',
      languages: [
        {
          value: 'de',
          text: this.$t('Headers.german'),
        },
        {
          value: 'en',
          text: this.$t('Headers.english'),
        },
      ],
      newOrg: false,
      enable: false,
      errorsPhone: '',
    }
  },
  created() {
    if (this.new_org !== 'new-org') {
      this.newOrg = false
      if (this.$route.path !== '/register') {
        this.$router.push('/register')
      }
    } else {
      this.newOrg = true
    }
    this.$store.commit('set_loading', false)
  },
  computed: {
    currentLanguage() {
      return this.$store.state.auth.language || 'de'
    },
    loadingState() {
      return this.$store.state.loading
    },
  },
  methods: {
    checkPhone(number, phone) {
      if (phone.isValid) {
        if (phone.type !== 'mobile') {
          this.errorsPhone = i18n.t('FormValidation.mobile')
          this.$store.commit('set_loading', false)
          this.$emit('checked', false)
          return false
        }
        this.errorsPhone = ''
        this.user.phone = phone.number.e164
        this.$emit('checked', true)
        return true
      } else {
        this.errorsPhone = i18n.t('FormValidation.phone')
        this.$store.commit('set_loading', false)
        this.$emit('checked', false)
        return false
      }
    },
    cancel() {
      this.$router.push('/adminLogin')
    },
    setEnable() {
      this.enable = this.privacyCheck
    },
    async register() {
      if (!this.$refs.form.validate()) {
        this.$emit('done')
        this.$store.commit('set_loading', false)
        return false
      }

      this.$store.commit('set_loading', true)
      this.user.language = this.currentLanguage
      delete this.user.confirm_password
      delete this.user.language_id

      if (this.newOrg) {
        this.user.account_role = 'admin'
        var newCompany = {
          organization: { organization_name: this.company.name },
          account_administrator: this.user,
          trial_version: true,
          license: {
            expires_at: '2023-07-09 16:36:08.115843+01',
            ses_signature_limit: 5000,
            aes_signature_limit: 5000,
            qes_signature_limit: 0,
          },
        }
        this.$store
          .dispatch('auth/registerOrg', newCompany)
          .catch((err) => {
            return log('AdminPage.Register.register() - ' + err.message, 'error')
          })
          .finally(() => {
            this.user.password = ''
            this.user.confirm_password = ''
            this.$router.push('/adminLogin')
          })
      } else {
        this.user.account_role = 'admin'
        var newUser = {
          organizationAdministrator: this.user,
          license_key_id: this.license_key_id,
        }
        this.$store
          .dispatch('auth/register', newUser)
          .catch((err) => {
            log('AdminPage.Register.register() - ' + err.message, 'error')
          })
          .then(() => {
            log(
              'AdminPage.Register.register() - register new User: ' + newUser,
              'info'
            )
          })
          .finally(() => {
            this.user.password = ''
            this.user.confirm_password = ''
            this.$router.push('/adminLogin')
          })
      }
    },
  },
}
</script>
